import type { InventoryItem } from '../resources/types';
import type { ConditionHandler } from '../../libs/offerSystem/offerSystemTypes';

export enum ConditionType {
  Shown = 'Shown',
  Bought = 'Bought',
  BoughtInstanceLimit = 'BoughtInstanceLimit',
  BoughtCooldownHours = 'BoughtCooldownHours',
  ShownCooldownHours = 'ShownCooldownHours',
  Band = 'Band',
  Level = 'Level',
  ActiveDays = 'ActiveDays',
  DaysSinceLastPurchase = 'DaysSinceLastPurchase',
  InventoryBalance = 'InventoryBalance',
  Time = 'Time',
  BoughtTag = 'BoughtTag',
  ShownCooldownTag = 'ShownCooldownTag',
  Trigger = 'Trigger',
  PurchaseCooldownHours = 'PurchaseCooldownHours',
  LegacyBand = 'LegacyBand',
  HS774Distribution = 'HS774Distribution',
  Tier1Country = 'Tier1',
  ActiveSeasonPassWithoutPremium = 'ActiveSeasonPassWithoutPremium',
  Payer = 'Payer',
}

export function simpleAmountConditionHandler(condition: SimpleAmountCondition, valueToTestAgainst: number): boolean {
  if (condition.operator === 'EQUAL') {
    return condition.value === valueToTestAgainst;
  }

  if (condition.operator === 'LESS') {
    return valueToTestAgainst < condition.value;
  }

  if (condition.operator === 'LESS OR EQUAL') {
    return valueToTestAgainst <= condition.value;
  }

  if (condition.operator === 'GREATER') {
    return valueToTestAgainst > condition.value;
  }

  if (condition.operator === 'GREATER OR EQUAL') {
    return valueToTestAgainst >= condition.value;
  }

  return false;
}

export interface SimpleAmountCondition {
  operator: 'GREATER OR EQUAL' | 'GREATER' | 'EQUAL' | 'LESS' | 'LESS OR EQUAL';
  value: number;
}

export interface SimpleBooleanCondition {
  value: boolean;
}

export interface ShownLimitCondition extends SimpleAmountCondition {
  type: ConditionType.Shown;
  productId: string;
}

export interface BoughtLifetimeLimit extends SimpleAmountCondition {
  type: ConditionType.Bought;
  productId: string;
}

export interface BoughtInstanceLimit extends SimpleAmountCondition {
  type: ConditionType.BoughtInstanceLimit;
}

export interface BoughtCooldownHours extends SimpleAmountCondition {
  type: ConditionType.BoughtCooldownHours;
  productId: string;
}

export interface ShownCooldownHours extends SimpleAmountCondition {
  type: ConditionType.ShownCooldownHours;
  passOnNotShown: boolean;
  productId: string;
}

export interface Band extends SimpleAmountCondition {
  type: ConditionType.Band;
}

export interface ActiveDays extends SimpleAmountCondition {
  type: ConditionType.ActiveDays;
}

export interface DaysSinceLastPurchase extends SimpleAmountCondition {
  type: ConditionType.DaysSinceLastPurchase;
  strict: boolean;
}

export interface Level extends SimpleAmountCondition {
  type: ConditionType.Level;
}

export interface InventoryBalance extends SimpleAmountCondition {
  type: ConditionType.InventoryBalance;
  inventoryType: InventoryItem;
}

export interface Time extends SimpleAmountCondition {
  type: ConditionType.Time;
}

export interface BoughtTag extends SimpleAmountCondition {
  type: ConditionType.BoughtTag;
  tag: string;
}

export interface ShownCooldownTag extends SimpleAmountCondition {
  type: ConditionType.ShownCooldownTag;
  passOnNotShown: boolean;
  tag: string;
}

export interface Trigger {
  type: ConditionType.Trigger;
  trigger: string;
}

export interface PurchaseCooldownHours extends SimpleAmountCondition {
  type: ConditionType.PurchaseCooldownHours;
  passOnNoPurchase: boolean;
  productId: string;
}

export interface LegacyBand extends SimpleAmountCondition {
  type: ConditionType.LegacyBand;
}

export interface HS774Distribution {
  type: ConditionType.HS774Distribution;
  distribution: number;
}

export interface Tier1Country extends SimpleBooleanCondition {
  type: ConditionType.Tier1Country;
}

export interface ActiveSeasonPassWithoutPremium extends SimpleBooleanCondition {
  type: ConditionType.ActiveSeasonPassWithoutPremium;
}

export interface Payer extends SimpleBooleanCondition {
  type: ConditionType.Payer;
}

export type Condition =
  | ShownLimitCondition
  | BoughtLifetimeLimit
  | BoughtInstanceLimit
  | BoughtCooldownHours
  | ShownCooldownHours
  | Band
  | ActiveDays
  | DaysSinceLastPurchase
  | Level
  | InventoryBalance
  | Time
  | BoughtTag
  | ShownCooldownTag
  | Trigger
  | PurchaseCooldownHours
  | LegacyBand
  | HS774Distribution
  | Tier1Country
  | ActiveSeasonPassWithoutPremium
  | Payer;

export type ConditionHandlersObjects<K, GD> = {
  [ConditionType.Shown]: ConditionHandler<K, ShownLimitCondition, GD>;
  [ConditionType.Bought]: ConditionHandler<K, BoughtLifetimeLimit, GD>;
  [ConditionType.BoughtInstanceLimit]: ConditionHandler<K, BoughtInstanceLimit, GD>;
  [ConditionType.BoughtCooldownHours]: ConditionHandler<K, BoughtCooldownHours, GD>;
  [ConditionType.ShownCooldownHours]: ConditionHandler<K, ShownCooldownHours, GD>;
  [ConditionType.Band]: ConditionHandler<K, Band, GD>;
  [ConditionType.ActiveDays]: ConditionHandler<K, ActiveDays, GD>;
  [ConditionType.DaysSinceLastPurchase]: ConditionHandler<K, DaysSinceLastPurchase, GD>;
  [ConditionType.Level]: ConditionHandler<K, Level, GD>;
  [ConditionType.InventoryBalance]: ConditionHandler<K, InventoryBalance, GD>;
  [ConditionType.Time]: ConditionHandler<K, Time, GD>;
  [ConditionType.BoughtTag]: ConditionHandler<K, BoughtTag, GD>;
  [ConditionType.ShownCooldownTag]: ConditionHandler<K, ShownCooldownTag, GD>;
  [ConditionType.Trigger]: ConditionHandler<K, Trigger, GD>;
  [ConditionType.PurchaseCooldownHours]: ConditionHandler<K, PurchaseCooldownHours, GD>;
  [ConditionType.LegacyBand]: ConditionHandler<K, LegacyBand, GD>;
  [ConditionType.HS774Distribution]: ConditionHandler<K, HS774Distribution, GD>;
  [ConditionType.Tier1Country]: ConditionHandler<K, Tier1Country, GD>;
  [ConditionType.ActiveSeasonPassWithoutPremium]: ConditionHandler<K, ActiveSeasonPassWithoutPremium, GD>;
  [ConditionType.Payer]: ConditionHandler<K, Payer, GD>;
};
